import { Box, Text, VStack } from '@chakra-ui/react'
import { AllergyEntry } from '@hb/shared'

export interface GynAllergyDisplayRow {
  id: string
  label: string
  anaphylaxis?: string | null
  details?: string | null
  specify?: string | null
}

interface MedicationAllergyItem {
  name?: string
  anaphylaxis?: string
  details?: string
}

const formatYesNoUnknown = (v?: string | null) => {
  if (v == null || v === '') return null
  if (v === 'yes') return 'Yes'
  if (v === 'no') return 'No'
  if (v === 'unknown') return 'Unknown'
  return v
}

export const FOOD_KEYS: { key: string; label: string }[] = [
  { key: 'egg', label: 'Egg' },
  { key: 'tree-nuts', label: 'Tree nuts' },
  { key: 'peanuts', label: 'Peanuts' },
  { key: 'soy', label: 'Soy' },
  { key: 'fish', label: 'Fish' },
  { key: 'shellfish', label: 'Shellfish' },
  { key: 'other-food', label: 'Other food' },
]

export const ENV_KEYS: { key: string; label: string }[] = [
  { key: 'grass-hay', label: 'Grass / hay' },
  { key: 'mold', label: 'Mold' },
  { key: 'pollen', label: 'Pollen' },
  { key: 'ragweed', label: 'Ragweed' },
  { key: 'flowers', label: 'Flowers' },
  { key: 'dust-or-dust-mites', label: 'Dust / dust mites' },
  { key: 'other-environmental', label: 'Other environmental' },
]

const pushStructuredAllergy = (
  rows: GynAllergyDisplayRow[],
  prefix: string,
  key: string,
  defaultLabel: string,
  entry?: AllergyEntry,
) => {
  if (!entry?.hasAllergy) return
  const isOther = key === 'other-food' || key === 'other-environmental' || key === 'other'
  const label = isOther && entry.specify?.trim() ? entry.specify.trim() : defaultLabel
  rows.push({
    id: `${prefix}-${key}`,
    label,
    anaphylaxis: formatYesNoUnknown(entry.anaphylaxis),
    details: entry.details?.trim() || null,
    specify: entry.specify?.trim() || null,
  })
}

export const getGynAllergyDisplayRows = (allergies: unknown): GynAllergyDisplayRow[] => {
  if (!allergies || typeof allergies !== 'object') return []
  const a = allergies as Record<string, unknown>
  const rows: GynAllergyDisplayRow[] = []

  const food = a.food as Record<string, unknown> | undefined
  if (food?.hasFoodAllergies) {
    for (const { key, label } of FOOD_KEYS) {
      pushStructuredAllergy(rows, 'food', key, label, food[key] as AllergyEntry | undefined)
    }
  }

  const env = a.enviromental as Record<string, unknown> | undefined
  if (env?.hasEnvironmentalAllergies) {
    for (const { key, label } of ENV_KEYS) {
      pushStructuredAllergy(rows, 'env', key, label, env[key] as AllergyEntry | undefined)
    }
  }

  const other = a.other as Record<string, unknown> | undefined
  if (other) {
    pushStructuredAllergy(rows, 'other', 'latex', 'Latex', other.latex as AllergyEntry)
    pushStructuredAllergy(
      rows,
      'other',
      'adhesives',
      'Adhesives / bandages',
      other.adhesives as AllergyEntry,
    )
    pushStructuredAllergy(rows, 'other', 'other', 'Other', other.other as AllergyEntry)

    const medList = other.medicationsList as MedicationAllergyItem[] | undefined
    if (other.medications && Array.isArray(medList)) {
      medList.forEach((item, index) => {
        const name = item?.name?.trim()
        if (!name) return
        rows.push({
          id: `medication-${index}-${name}`,
          label: name,
          anaphylaxis: formatYesNoUnknown(item.anaphylaxis),
          details: item.details?.trim() || null,
          specify: null,
        })
      })
    }
  }

  return rows
}

export const AllergyDetailBody = ({ row }: { row: GynAllergyDisplayRow }) => {
  const showSpecify = row.specify && row.specify !== row.label
  const hasDetail = row.anaphylaxis || row.details || showSpecify
  return (
    <VStack align="stretch" spacing={2} fontSize="sm">
      {row.anaphylaxis ? (
        <Box>
          <Text fontWeight={600} color="gray.600" _dark={{ color: 'gray.400' }}>
            Anaphylaxis
          </Text>
          <Text>{row.anaphylaxis}</Text>
        </Box>
      ) : null}
      {row.details ? (
        <Box>
          <Text fontWeight={600} color="gray.600" _dark={{ color: 'gray.400' }}>
            Reaction / details
          </Text>
          <Text whiteSpace="pre-wrap">{row.details}</Text>
        </Box>
      ) : null}
      {showSpecify ? (
        <Box>
          <Text fontWeight={600} color="gray.600" _dark={{ color: 'gray.400' }}>
            Specified
          </Text>
          <Text whiteSpace="pre-wrap">{row.specify}</Text>
        </Box>
      ) : null}
      {!hasDetail ? <Text color="gray.500">No additional details on file.</Text> : null}
    </VStack>
  )
}
